import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, FolderOpen, LogOut } from 'lucide-react';

/**
 * Avatar + dropdown in the marketplace header. Replaces the bare "Studies / Log out"
 * links: an always-visible Log out is both noisy and easy to hit by accident, and
 * there was nowhere to show WHO is signed in.
 */
const initialsOf = (name?: string, email?: string): string => {
  const source = (name || '').trim() || (email || '').split('@')[0] || '';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const letters = parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[1][0];
  return letters.toUpperCase();
};

const ProfileMenu: React.FC<{
  email?: string;
  name?: string;
  onStudies: () => void;
  onLogout: () => void;
}> = ({ email, name, onStudies, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape — a dropdown that can only be dismissed by
  // re-clicking the trigger feels broken.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        // The avatar itself stays 32px — the hit area around it grows to the 44px
        // thumb minimum on touch widths without visually enlarging the chrome.
        className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-full p-0.5 transition-colors hover:bg-black/5 md:min-h-0 md:min-w-0 md:justify-start"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-[12px] font-semibold text-white">
          {initialsOf(name, email)}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[#6b6b6b] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-60 overflow-hidden rounded-xl border border-black/10 bg-white shadow-lg"
        >
          <div className="border-b border-black/10 px-4 py-3">
            {name && <div className="truncate text-[13px] font-medium text-[#111]">{name}</div>}
            {email && <div className="truncate text-[12px] text-[#6b6b6b]">{email}</div>}
          </div>
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onStudies();
            }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[13px] text-[#111] transition-colors hover:bg-[#f3f4f6]"
          >
            <FolderOpen className="h-4 w-4 text-[#6b6b6b]" /> My studies
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="flex w-full items-center gap-2 border-t border-black/10 px-4 py-2.5 text-left text-[13px] text-[#111] transition-colors hover:bg-[#f3f4f6]"
          >
            <LogOut className="h-4 w-4 text-[#6b6b6b]" /> Log out
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;
